import React, { FC, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { Image } from '@/atoms/Image'
import { CaptionMD, LabelMD, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { Episode } from '@/services/ProjectsService'
import { Project, Episode as FederationEpisode } from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface GuildJoinCTAProps {
  episode?: Episode | FederationEpisode
  project?: Pick<Project, 'name' | 'slug'>
  moviePosterPath?: string | null
  title?: string
  subText?: string
}

export const GuildJoinCTA: FC<GuildJoinCTAProps> = React.memo(
  ({ episode, project, moviePosterPath, title, subText }) => {
    const [hasDismissed, setHasDismissed] = useState(false)
    const { t } = useTranslate('watch')
    const track = useSafeTrack()
    const { playing, playingProgress } = useAngelPlayerContext()

    const handleClick = () => {
      track('Clicked Guild Join Content Shared Player CTA Button', {
        guid: episode?.guid,
        episode,
      })
    }

    const renderCtaTime = useMemo(() => {
      if (!episode?.source?.duration) {
        return null
      }
      if (episode?.source?.credits) {
        return episode.source.credits
      } else {
        return episode?.source.duration - 60
      }
    }, [episode?.source?.credits, episode?.source?.duration])

    const showButtonTime = useMemo(() => {
      return (renderCtaTime ?? 0) + 30
    }, [renderCtaTime])
    if (!project) return null

    const variants = {
      open: {
        opacity: 1,
        y: 0,
        transition: {
          type: 'spring',
          bounce: 0.2,
          duration: 0.6,
        },
      },
      closed: { y: 40, opacity: 0, display: 'none' },
    }

    const movieTitle = project.name
    const shouldRenderCta = returnShouldRenderCta(playingProgress, renderCtaTime)
    const showCta = returnShowCta(playing, playingProgress, showButtonTime)

    if (!shouldRenderCta || hasDismissed) return null

    return (
      <div
        id="guild-player-button"
        className="[w-92%] absolute left-1/2 top-1/4 z-40 min-w-[350px] max-w-[480px] -translate-x-1/2 -translate-y-1/4 text-[10px] text-white sm:text-base md:top-1/2 md:w-[98%] md:-translate-y-1/2"
      >
        <motion.div
          animate={showCta ? 'open' : 'closed'}
          variants={variants}
          className="flex items-center justify-center"
        >
          <div
            className="relative rounded-2xl p-1"
            style={{
              background: 'linear-gradient(to bottom right, #EDD7C6, #D6A177, #FEDEC6, #D48D59, #A66141, #612B12)',
            }}
          >
            <div
              className="absolute -right-3 -top-3 cursor-pointer rounded-full p-[2px]"
              style={{
                background: 'linear-gradient(to bottom right, #EDD7C6, #D6A177, #FEDEC6, #D48D59, #A66141, #612B12)',
              }}
              onClick={() => setHasDismissed(true)}
            >
              <div className="rounded-full bg-core-gray-900 p-1">
                <CloseIcon color="white" size={20} />
              </div>
            </div>
            <div className="flex flex-row gap-2 rounded-xl bg-core-gray-900 p-2 md:gap-4 md:p-4">
              <div className="shrink-0">
                <Image
                  src={getCloudinaryImageUrl({
                    path: moviePosterPath ?? '',
                    width: 150,
                    height: 225,
                    transforms: 'c_fill',
                  })}
                  alt={`${movieTitle} poster`}
                  width={83}
                  height={148}
                  className="rounded-xl"
                />
              </div>

              <div className="flex flex-col justify-between gap-4">
                <TitleXS weight="semibold">
                  {title ?? (
                    <Translate i18nKey="GuildJoinCTATitle" t={t} values={{ movieTitle }}>
                      Did you enjoy <i>{{ movieTitle }}</i>?
                    </Translate>
                  )}
                </TitleXS>
                <CaptionMD>
                  {subText ?? (
                    <Translate i18nKey="guildJoinCTAMessage" t={t}>
                      Join the Guild to support and watch series, films, and comedy specials that amplify light!
                    </Translate>
                  )}
                </CaptionMD>
                <LinkButton
                  onClick={handleClick}
                  variant="white"
                  external
                  target="_blank"
                  href={`${paths.guild.join}/${project.slug}`}
                  className="text-core-gray-950"
                >
                  <div className="flex min-h-6 flex-row items-center">
                    <GuildIconMono color="black" size={20} />
                    <LabelMD className="pl-1">{t('joinTheGuild', 'Join The Guild')}</LabelMD>
                  </div>
                </LinkButton>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    )
  },
)

GuildJoinCTA.displayName = 'React.memo(SneakPeakCTA)'

function returnShowCta(playing: boolean, playingProgress: number, showButtonTime: number) {
  return playing && playingProgress > showButtonTime
}

function returnShouldRenderCta(playingProgress: number, renderCtaTime: number | null) {
  return playingProgress > (renderCtaTime ?? 0)
}
